import moment from "moment/moment";

// Types
import { TDataProperty } from "@DataPages/types/salesOperations/salesLog/utils";
import { useSalesLogChart } from "@DataPages/context/salesOperations/salesLog/SalesLogChartsContext.tsx";
import { ICommissionCycle } from "@/Common/api/types/CommissionCycle";

type Payload = {
	day: number;
	[key: string]: unknown;
};

type CandleStickBarProps = {
	x?: number;
	y?: number;
	width?: number;
	height?: number;
	payload?: Payload;
	dataProperty: TDataProperty;
	selected: ICommissionCycle;
	lastActiveCommissionCycle: ICommissionCycle;
	daysInCurrentMonth: number;
	isLastDayOfMonth: boolean;
	isDateBeforeCurrentDate: (endDate: Date, day: number) => boolean;
};

const isLastDayOfMonthWithNoData = ({
	daysInCurrentMonth,
	isLastDayOfMonth,
	selected,
	lastActiveCommissionCycle,
	payload = {
		day: 0,
	},
}: Omit<CandleStickBarProps, "dataProperty" | "isDateBeforeCurrentDate">) => {
	return (
		payload.day === daysInCurrentMonth &&
		!isLastDayOfMonth &&
		moment(selected.startDate).isBetween(lastActiveCommissionCycle.startDate, lastActiveCommissionCycle.endDate, null, "[]") &&
		!moment(selected.endDate).isBefore(moment())
	);
};

function determineBarAttributes({
	payload,
	dataProperty,
	y,
	height,
}: Required<Pick<CandleStickBarProps, "payload" | "dataProperty" | "y" | "height">>) {
	let fillColor = "";
	let isIncremental = false;
	let barY = 0;

	if (payload?.[dataProperty]) {
		const [min, max] = (payload[dataProperty] as [number, number]) || [0, 0];
		if (min <= max) {
			fillColor = "#2357BE";
			isIncremental = true;
			barY = y;
		} else {
			barY = y + height;
			fillColor = "#B3B3B3";
		}
	}

	return { fillColor, isIncremental, barY };
}

export default function CandleStickBar(props: Readonly<CandleStickBarProps>) {
	const { filteredRows } = useSalesLogChart();

	const {
		x = 0,
		y = 0,
		width = 0,
		height = 0,
		payload = {
			day: 0,
		},
		dataProperty,
		selected,
		lastActiveCommissionCycle,
		daysInCurrentMonth,
		isLastDayOfMonth = false,
		isDateBeforeCurrentDate,
	} = props;

	const attributes = determineBarAttributes({ payload, dataProperty, y, height });
	const { barY, fillColor, isIncremental } = attributes;

	const curveSize = 9; // Fixed curve size for the rounded corners
	let pathData;
	const paceBarWidth = 12;
	if (!isIncremental) {
		// Upside-down path (rounded corners at the top)
		pathData = `
				M ${x} ${y} 
				Q ${x} ${y + curveSize} ${x + paceBarWidth / 2} ${y + curveSize} 
				L ${x + paceBarWidth / 2} ${y + curveSize} 
				Q ${x + paceBarWidth} ${y + curveSize} ${x + paceBarWidth} ${y} 
				L ${x + paceBarWidth} ${y + height}
				L ${x} ${y + height}
			`;
	} else {
		// Original path (rounded corners at the top)
		pathData = `
				M ${x} ${y + curveSize} 
				Q ${x} ${y} ${x + paceBarWidth / 2} ${y} 
				L ${x + paceBarWidth / 2} ${y}
				Q ${x + paceBarWidth} ${y} ${x + paceBarWidth} ${y + curveSize}
				L ${x + paceBarWidth} ${y + height}
				L ${x} ${y + height}
        	`;
	}

	if (filteredRows.length === 0) {
		return null;
	}

	// Check if the current payload day is the last day of the month
	// and if there's no data for that day.
	if (
		isLastDayOfMonthWithNoData({
			payload,
			daysInCurrentMonth,
			isLastDayOfMonth,
			selected,
			lastActiveCommissionCycle,
		})
	) {
		return (
			<g>
				<defs>
					<linearGradient id="incrementalGradient" x1="0" y1="0" x2="0" y2="1">
						<stop offset="5%" stopColor="#2357BE" />
						<stop offset="20%" stopColor="#295BBD" />
						<stop offset="40%" stopColor="#3D67BC" />
						<stop offset="60%" stopColor="#5C7BB9" />
						<stop offset="80%" stopColor="#8897B6" />
						<stop offset="100%" stopColor="#B3B3B3" />
					</linearGradient>
				</defs>

				<path d={pathData} fill={"url(#incrementalGradient)"} />
			</g>
		);
	}

	// Show gray fixed length bar if there's no data for that specific day and if that day is before current day
	if (isDateBeforeCurrentDate(selected.endDate, payload?.day ?? 0) && height === 0 && selected.startDate) {
		return <rect x={x + 2} y={barY} rx={6} ry={6} width={width - 3} height={1} fill={"#6c6c6c"} />;
	}

	return <rect x={x + 2} y={barY} rx={6} ry={6} width={width - 3} height={Math.abs(height)} fill={fillColor} />;
}
